/**
 * AEGIS-12 seed — team copilot & performance intelligence.
 *
 * Seeds six demo team members across three roles (dispatcher, picker,
 * cxRep) with 30 days of daily activity, rolled-up performance profiles,
 * a handful of AEGIS-authored coaching insights, and manager-set goals.
 *
 * The cxRep and picker profiles are included so the dashboard has cross-
 * role benchmarks even when a single tenant only runs one workflow.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const now = new Date();
const iso = (d) => new Date(d).toISOString();
const daysAgoDate = (n) => { const d = new Date(now); d.setDate(d.getDate() - n); return d; };
const yyyymmdd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const universal = (record, source = 'seed') => ({
  ...record,
  tenantId: DEMO_TENANT_ID,
  createdAt: iso(daysAgoDate(30)),
  createdBy: 'system',
  updatedAt: iso(now),
  updatedBy: 'system',
  isDeleted: false,
  version: 1,
  source,
});

// Demo team — six members across three roles. userIds intentionally stable
// so AEGIS-12 seed data links to existing users if they already exist.
export const SEED_TEAM_MEMBERS = [
  { userId: 'user-alex-dispatch',  name: 'Alex Morgan',   role: 'dispatcher', managerId: 'user-manager-01', hiredAt: '2024-03-10' },
  { userId: 'user-priya-dispatch', name: 'Priya Shah',    role: 'dispatcher', managerId: 'user-manager-01', hiredAt: '2023-08-21' },
  { userId: 'user-luis-picker',    name: 'Luis Reyes',    role: 'picker',     managerId: 'user-manager-02', hiredAt: '2024-01-12' },
  { userId: 'user-amy-picker',     name: 'Amy Chen',      role: 'picker',     managerId: 'user-manager-02', hiredAt: '2022-11-04' },
  { userId: 'user-dev-cx',         name: 'Devon Patel',   name2: 'D. Patel',  role: 'cxRep',  managerId: 'user-manager-03', hiredAt: '2024-06-02' },
  { userId: 'user-sara-cx',        name: 'Sara Lindgren', role: 'cxRep',      managerId: 'user-manager-03', hiredAt: '2023-02-18' },
];

// Generate 30 days of synthetic daily activity per user. Pattern encodes
// an obvious regression in Alex (carrier diversity + tender acceptance
// decay over the last 10 days) and a personal best in Amy (pick rate).
function activityFor(userId, role, daysBack) {
  const day = daysAgoDate(daysBack);
  const date = yyyymmdd(day);
  const base = {
    activityId: `act-${userId}-${date}`,
    userId,
    role,
    date,
    dayOfWeek: day.getDay(),
  };
  if (role === 'dispatcher') {
    // Alex: decay signal starting at day 10 back → today
    const alexDecay = userId === 'user-alex-dispatch' && daysBack <= 10 ? 1 - (10 - daysBack) * 0.025 : 1;
    const priyaLift = userId === 'user-priya-dispatch' ? 1.05 : 1;
    return {
      ...base,
      metrics: {
        loadsHandled: Math.round((16 + (daysBack % 5) - (daysBack % 3)) * priyaLift),
        tendersSent: Math.round((22 + (daysBack % 4)) * priyaLift),
        tendersAccepted: Math.round((18 + (daysBack % 4)) * alexDecay * priyaLift),
        tenderAcceptanceRate: Math.max(0.62, Math.min(0.95, 0.82 * alexDecay * priyaLift)),
        carriersUsed: userId === 'user-alex-dispatch' && daysBack <= 10 ? 2 : 6 + (daysBack % 3),
        carrierDiversity: userId === 'user-alex-dispatch' && daysBack <= 10 ? 0.22 : 0.58 + (daysBack % 4) * 0.03,
        avgExceptionResolutionMin: 18 + (daysBack % 5) + (userId === 'user-alex-dispatch' && daysBack <= 10 ? 12 : 0),
        costPerLoadDelta: userId === 'user-alex-dispatch' && daysBack <= 10 ? 0.06 : -0.02 + (daysBack % 3) * 0.005,
        avgResponseTimeMin: 7 + (daysBack % 3),
      },
    };
  }
  if (role === 'picker') {
    const amyLift = userId === 'user-amy-picker' && daysBack <= 3 ? 1.18 : 1; // personal best this week
    const luisDip = userId === 'user-luis-picker' && daysBack <= 5 ? 0.94 : 1;
    return {
      ...base,
      metrics: {
        picksPerHour: Math.round((72 + (daysBack % 6)) * amyLift * luisDip),
        totalPicks: Math.round((540 + (daysBack % 9) * 10) * amyLift * luisDip),
        accuracyRate: Math.max(0.94, Math.min(0.998, 0.986 - (userId === 'user-luis-picker' && daysBack <= 5 ? 0.01 : 0) + (daysBack % 3) * 0.001)),
        shortPickRate: userId === 'user-luis-picker' && daysBack <= 5 ? 0.031 : 0.012 + (daysBack % 4) * 0.001,
        zoneEfficiency: 0.82 - (userId === 'user-luis-picker' && daysBack <= 5 ? 0.06 : 0) + (daysBack % 5) * 0.005,
      },
    };
  }
  // cxRep
  const devonLearningCurve = userId === 'user-dev-cx' && daysBack >= 20 ? 1.3 : 1; // slower earlier
  const saraBurnout = userId === 'user-sara-cx' && daysBack <= 7 ? 1.2 : 1; // rising response time + breach
  return {
    ...base,
    metrics: {
      ticketsHandled: Math.round(24 - (daysBack % 4) * 1.2),
      avgResponseTimeMin: Math.round((6 + (daysBack % 3)) * devonLearningCurve * saraBurnout),
      slaBreachCount: userId === 'user-sara-cx' && daysBack <= 7 ? 2 + (daysBack % 2) : (daysBack % 5 === 0 ? 1 : 0),
      slaBreachRate: userId === 'user-sara-cx' && daysBack <= 7 ? 0.08 + (daysBack % 3) * 0.01 : 0.02 + (daysBack % 4) * 0.005,
      customerNps: userId === 'user-sara-cx' && daysBack <= 7 ? 38 : 61 + (daysBack % 6),
      escalationRate: userId === 'user-sara-cx' && daysBack <= 7 ? 0.18 : 0.06 + (daysBack % 3) * 0.01,
    },
  };
}

export const SEED_AEGIS_USER_ACTIVITY = (() => {
  const out = [];
  for (const m of SEED_TEAM_MEMBERS) {
    for (let d = 0; d < 30; d++) out.push(universal(activityFor(m.userId, m.role, d)));
  }
  return out;
})();

// Rolled-up profiles — one per member. Captures 30-day baseline, 7-day
// trend, personal percentile within role, and AEGIS-detected signals.
function profileFor(member) {
  const activity = SEED_AEGIS_USER_ACTIVITY.filter(a => a.userId === member.userId);
  const avg = (key) => activity.reduce((s, a) => s + (a.metrics[key] || 0), 0) / activity.length;
  const avgLast7 = (key) => activity.slice(0, 7).reduce((s, a) => s + (a.metrics[key] || 0), 0) / 7;

  const base = {
    profileId: `prof-${member.userId}`,
    userId: member.userId,
    name: member.name,
    role: member.role,
    managerId: member.managerId,
    hiredAt: member.hiredAt,
    rollingWindowDays: 30,
    generatedAt: iso(now),
  };

  if (member.role === 'dispatcher') {
    const trend = avgLast7('tenderAcceptanceRate') < avg('tenderAcceptanceRate') * 0.92 ? 'declining' : 'stable';
    return {
      ...base,
      metrics: {
        loadsHandled: Math.round(avg('loadsHandled') * 30),
        avgTenderAcceptanceRate: Number(avg('tenderAcceptanceRate').toFixed(3)),
        carrierDiversityScore: Number(avg('carrierDiversity').toFixed(3)),
        avgExceptionResolutionMin: Math.round(avg('avgExceptionResolutionMin')),
        costPerLoadDelta: Number(avg('costPerLoadDelta').toFixed(3)),
        rolling7DayAcceptance: Number(avgLast7('tenderAcceptanceRate').toFixed(3)),
      },
      trend,
      percentileRank: member.userId === 'user-priya-dispatch' ? 82 : member.userId === 'user-alex-dispatch' ? 41 : 55,
      aegisSignals: member.userId === 'user-alex-dispatch'
        ? ['carrier_diversity_decline', 'acceptance_rate_regression', 'cost_per_load_rising']
        : member.userId === 'user-priya-dispatch' ? ['personal_best_candidate'] : [],
    };
  }
  if (member.role === 'picker') {
    return {
      ...base,
      metrics: {
        avgPicksPerHour: Math.round(avg('picksPerHour')),
        accuracyRate: Number(avg('accuracyRate').toFixed(4)),
        shortPickRate: Number(avg('shortPickRate').toFixed(4)),
        zoneEfficiency: Number(avg('zoneEfficiency').toFixed(3)),
        rolling7DayPickRate: Math.round(avgLast7('picksPerHour')),
      },
      trend: avgLast7('picksPerHour') > avg('picksPerHour') * 1.08 ? 'rising'
           : avgLast7('picksPerHour') < avg('picksPerHour') * 0.95 ? 'declining' : 'stable',
      percentileRank: member.userId === 'user-amy-picker' ? 91 : member.userId === 'user-luis-picker' ? 48 : 60,
      aegisSignals: member.userId === 'user-amy-picker'
        ? ['personal_best_candidate', 'accuracy_above_benchmark']
        : member.userId === 'user-luis-picker' ? ['short_pick_rate_rising', 'zone_efficiency_declining'] : [],
    };
  }
  // cxRep
  return {
    ...base,
    metrics: {
      avgResponseTimeMin: Math.round(avg('avgResponseTimeMin')),
      slaBreachRate: Number(avg('slaBreachRate').toFixed(3)),
      avgCustomerNps: Math.round(avg('customerNps')),
      escalationRate: Number(avg('escalationRate').toFixed(3)),
      rolling7DayResponseMin: Math.round(avgLast7('avgResponseTimeMin')),
    },
    trend: avgLast7('avgResponseTimeMin') > avg('avgResponseTimeMin') * 1.15 ? 'declining'
         : avgLast7('avgResponseTimeMin') < avg('avgResponseTimeMin') * 0.92 ? 'rising' : 'stable',
    percentileRank: member.userId === 'user-sara-cx' ? 34 : member.userId === 'user-dev-cx' ? 62 : 55,
    aegisSignals: member.userId === 'user-sara-cx'
      ? ['response_time_regression', 'sla_breach_rising', 'burnout_signal']
      : member.userId === 'user-dev-cx' ? ['ramp_progress_positive'] : [],
  };
}

export const SEED_AEGIS_PERFORMANCE_PROFILES = SEED_TEAM_MEMBERS.map(m => universal(profileFor(m)));

// AEGIS coaching insights — one per member who has a signal worth raising.
export const SEED_AEGIS_COACHING_INSIGHTS = [
  universal({
    insightId: 'coach-001',
    userId: 'user-alex-dispatch',
    subjectName: 'Alex Morgan',
    role: 'dispatcher',
    week: yyyymmdd(daysAgoDate(0)).slice(0, 7),
    severity: 'high',
    title: 'Carrier diversity dropped 62% over 10 days',
    summary: 'Alex has concentrated 88% of tenders on two carriers (SWFT, USAT). Tender acceptance fell from 86% → 71% and cost-per-load rose ~6%.',
    recommendedAction: 'Review carrier options on Chicago→Dallas and Atlanta→Newark lanes. Bring two alternates back into rotation.',
    talkingPoints: [
      'Is there a relationship reason for the concentration, or is it convenience?',
      'SWFT OTP dropped to 82% this month — let\'s look at the data together.',
      'Goal: rebalance so no single carrier exceeds 40% of weekly tenders.',
    ],
    confidence: 0.87,
    status: 'open',
    source: 'aegis_auto',
    provider: 'primary',
  }),
  universal({
    insightId: 'coach-002',
    userId: 'user-sara-cx',
    subjectName: 'Sara Lindgren',
    role: 'cxRep',
    week: yyyymmdd(daysAgoDate(0)).slice(0, 7),
    severity: 'high',
    title: 'Early burnout signal — response time + SLA breach rising together',
    summary: 'Response time up 68%, SLA breach rate up 4x, NPS down 23 points in the last 7 days. Compound pattern typical of workload overload, not skill regression.',
    recommendedAction: 'Schedule a 1:1 this week. Consider rebalancing account load or adding tier-1 triage support.',
    talkingPoints: [
      'How\'s the workload feeling? Are there specific accounts taking disproportionate time?',
      'Is there anything going on outside of work we should be aware of?',
      'Offer: shift 2–3 high-volume accounts to Devon this week while we reset.',
    ],
    confidence: 0.79,
    status: 'open',
    source: 'aegis_auto',
    provider: 'primary',
  }),
  universal({
    insightId: 'coach-003',
    userId: 'user-amy-picker',
    subjectName: 'Amy Chen',
    role: 'picker',
    week: yyyymmdd(daysAgoDate(0)).slice(0, 7),
    severity: 'recognition',
    title: 'Personal best — 84 picks/hr with 99.2% accuracy',
    summary: 'Amy\'s rolling 7-day pick rate (84) is her highest in 12 months while accuracy held above 99%. Worth explicit recognition.',
    recommendedAction: 'Recognize in team standup. Ask Amy to share her approach — zone-B micro-batching is paying off.',
    talkingPoints: [
      'What changed this week? What can we bottle up for the team?',
      'Would you be open to mentoring Luis on zone-B pathing?',
    ],
    confidence: 0.92,
    status: 'open',
    source: 'aegis_auto',
    provider: 'primary',
  }),
  universal({
    insightId: 'coach-004',
    userId: 'user-luis-picker',
    subjectName: 'Luis Reyes',
    role: 'picker',
    week: yyyymmdd(daysAgoDate(0)).slice(0, 7),
    severity: 'medium',
    title: 'Short-pick rate rising in zone B',
    summary: 'Short picks 1.2% → 3.1% over 5 days, concentrated in zone B. Zone efficiency down 6 points. Likely a pathing or labeling issue, not accuracy.',
    recommendedAction: 'Walk zone B together this week. Check for label misprints or slot misplacement since last cycle count.',
    talkingPoints: [
      'What are you seeing in zone B the last week?',
      'Pair with Amy on her zone-B pathing for one shift.',
    ],
    confidence: 0.73,
    status: 'open',
    source: 'aegis_auto',
    provider: 'primary',
  }),
  universal({
    insightId: 'coach-005',
    userId: 'user-dev-cx',
    subjectName: 'Devon Patel',
    role: 'cxRep',
    week: yyyymmdd(daysAgoDate(0)).slice(0, 7),
    severity: 'recognition',
    title: 'Ramp ahead of schedule',
    summary: 'Devon\'s avg response time has improved 29% over the 30-day window — now within 12% of team benchmark at 3 months in role.',
    recommendedAction: 'Acknowledge the ramp explicitly. Consider adding one additional mid-tier account next cycle.',
    talkingPoints: [
      'What parts of onboarding actually helped vs. what felt like noise?',
      'Ready to take on one more account?',
    ],
    confidence: 0.84,
    status: 'open',
    source: 'aegis_auto',
    provider: 'primary',
  }),
];

export const SEED_AEGIS_PERFORMANCE_GOALS = [
  universal({
    goalId: 'goal-001',
    userId: 'user-alex-dispatch',
    role: 'dispatcher',
    metric: 'carrierDiversityScore',
    target: 0.55,
    floor: 0.40,
    periodDays: 30,
    setBy: 'user-manager-01',
    status: 'active',
    notes: 'Rebalance carrier rotation. Review monthly.',
  }),
  universal({
    goalId: 'goal-002',
    userId: 'user-sara-cx',
    role: 'cxRep',
    metric: 'avgResponseTimeMin',
    target: 8,
    floor: 15,
    periodDays: 14,
    setBy: 'user-manager-03',
    status: 'active',
    notes: 'Temporary easing window while workload rebalanced.',
  }),
  universal({
    goalId: 'goal-003',
    userId: 'user-luis-picker',
    role: 'picker',
    metric: 'shortPickRate',
    target: 0.015,
    floor: 0.025,
    periodDays: 21,
    setBy: 'user-manager-02',
    status: 'active',
    notes: 'Zone B remediation. Pair with Amy.',
  }),
];
